/**
 * The bar across the top of every page, in both shells.
 *
 * It is chrome rather than content: where you are, and the three controls that
 * belong to the application rather than to any screen. Everything a *page* has
 * to say about itself still belongs to that page's `PageHeader` below.
 *
 * **The trail names sections, never records.** Each crumb comes from the
 * route's own `staticData`, because a route knows its shape and not its
 * contents: "Admin / People" is a claim it can make without loading anything,
 * and "Admin / People / Alice Admin" is not. A route with no crumb — every
 * `$id` segment — contributes nothing, so a detail page shows the section it is
 * in and leaves naming itself to its own heading.
 *
 * Every crumb but the last links to the match's resolved pathname, which is
 * what lets the pages below drop the back-link they used to render: two
 * "People" one above the other, one of them clickable, is worse than either.
 *
 * The sidebar toggle appears here only while the sidebar is closed. Its home is
 * the sidebar's own header; this is where it goes when that header is not on
 * screen to hold it.
 */

import { Link, useMatches } from '@tanstack/react-router';
import { HelpCircle } from '@untitledui/icons';
import { type Sidebar, SidebarToggle } from './sidebar.tsx';
import { ThemeToggle } from './theme-toggle.tsx';

interface Crumb {
  label: string;
  /** The match's resolved pathname, so a crumb links to what it names. */
  to: string;
}

function useCrumbs(): Crumb[] {
  return useMatches({
    select: (matches) =>
      matches
        .map((match) => ({ label: match.staticData.crumb, to: match.pathname }))
        .filter((crumb): crumb is Crumb => typeof crumb.label === 'string'),
  });
}

/**
 * `aria-current="page"` on the last crumb rather than `<nav aria-label>` alone:
 * the trail is one list in which exactly one entry is where the reader is, and
 * that is the part a screen reader has no other way to learn.
 */
function Crumbs() {
  const crumbs = useCrumbs();
  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-sm">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li key={crumb.to} className="flex min-w-0 items-center gap-1.5">
              {index > 0 ? (
                <span aria-hidden="true" className="text-quaternary">
                  /
                </span>
              ) : null}
              {last ? (
                <span aria-current="page" className="truncate font-medium text-primary">
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.to}
                  className="truncate text-tertiary no-underline hover:text-primary"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function ShellBar({ sidebar }: { sidebar: Sidebar }) {
  return (
    <header className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 border-b border-secondary bg-secondary px-3">
      {sidebar.collapsed ? <SidebarToggle sidebar={sidebar} /> : null}
      <Crumbs />
      <span className="ml-auto flex shrink-0 items-center gap-0.5">
        <Link
          to="/help"
          title="How to use Matchbox OS"
          className="flex h-8 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-tertiary no-underline transition-colors hover:bg-primary-hover"
        >
          <HelpCircle size={16} />
          Help
        </Link>
        <ThemeToggle />
      </span>
    </header>
  );
}
